import React, { ReactNode, useId } from 'react';
import { Button } from '@components/Button';
import { useModalDialog } from '@/hooks/useModalDialog';

export interface ConfirmDialogProps {
  isOpen: boolean;
  /** Heading, e.g. "Batalkan perubahan?". */
  title: string;
  /** Supporting line under the title. */
  description?: ReactNode;
  /** Extra content between the description and the buttons (impact summaries). */
  children?: ReactNode;
  confirmLabel: string;
  /** Remix Icon class shown before the confirm label. */
  confirmIcon?: string;
  /** Omit for a one-button notice (a refusal the user can only acknowledge). */
  cancelLabel?: string;
  /** Confirm button styling. `danger` for destructive confirmations. */
  confirmVariant?: 'primary' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
  /**
   * Escape / backdrop. Defaults to `onCancel` — pass it explicitly when the
   * cancel button is itself the destructive action.
   */
  onDismiss?: () => void;
  /** In-flight request: both buttons are disabled. */
  isBusy?: boolean;
  /** Merged last onto the card. */
  className?: string;
}

/**
 * The one confirmation dialog (CLAUDE.md Rule 2). A native
 * `<dialog role="alertdialog">` opened with `showModal()`, so it lands in the
 * top layer and stacks *above* an already-open `SideBarModal` sheet — callers
 * inside a modal do not need to portal it anywhere.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  description,
  children,
  confirmLabel,
  confirmIcon,
  cancelLabel,
  confirmVariant = 'primary',
  onConfirm,
  onCancel,
  onDismiss,
  isBusy = false,
  className = '',
}) => {
  const titleId = useId();
  const descId = useId();
  const { setDialogRef, isNativeModal, dialogProps } = useModalDialog({
    isOpen,
    onRequestClose: onDismiss ?? onCancel,
  });

  if (!isOpen) return null;

  return (
    <dialog
      ref={setDialogRef}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      {...(description ? { 'aria-describedby': descId } : {})}
      {...dialogProps}
      className={`p-0 m-0 border-0 max-w-none max-h-none w-screen h-screen supports-[height:100dvh]:h-[100dvh] open:flex items-center justify-center ${
        isNativeModal
          ? 'bg-transparent backdrop:bg-black/50'
          : 'fixed inset-0 z-[60] bg-black/50'
      }`}
    >
      <div
        className={`w-full max-w-[calc(100vw-2rem)] sm:max-w-md bg-white rounded-lg shadow-xl p-6 animate-fade-in ${className}`}
      >
        <div className="flex items-start gap-3">
          <i
            className="ri-alert-line text-orange-500 text-2xl shrink-0"
            aria-hidden="true"
          />
          <div className="min-w-0">
            <h3 id={titleId} className="text-lg font-medium text-gray-900">
              {title}
            </h3>
            {description && (
              <p id={descId} className="mt-1 text-sm text-gray-600">
                {description}
              </p>
            )}
          </div>
        </div>

        {children}

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row">
          {cancelLabel && (
            <Button
              variant="white"
              onClick={onCancel}
              disabled={isBusy}
              className="h-11 w-full sm:w-auto sm:flex-1 text-gray-700"
            >
              {cancelLabel}
            </Button>
          )}
          <Button
            variant={confirmVariant}
            onClick={onConfirm}
            disabled={isBusy}
            className="h-11 w-full sm:w-auto sm:flex-1"
          >
            {confirmIcon && (
              <i className={`${confirmIcon} mr-1.5`} aria-hidden="true" />
            )}
            {confirmLabel}
          </Button>
        </div>
      </div>
    </dialog>
  );
};

export default ConfirmDialog;
